import React from "react";
import { Box, Button, Slider, Select, MenuItem } from "@mui/material";

/**
 * TrackRangeSlider - a reusable slider with label and reset button for selecting a track range (0-100).
 *
 * Props:
 *   value: { start: number, end: number }
 *   onChange: (newRange: { start, end }) => void
 *   onReset: () => void
 *   color: string (optional, default: '#00AEEF')
 *   label: string (optional, default: 'Track Range')
 *   corners: array (optional)
 *   selectedCornerIdx: number (optional)
 *   onCornerSelect: function (optional)
 */
export default function TrackRangeSlider({ value, onChange, onReset, color = '#00AEEF', label = 'Track Range', corners = [], selectedCornerIdx = null, onCornerSelect }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <h3 style={{ color: '#fff', margin: 0, fontWeight: 600, fontSize: '1.25rem', lineHeight: '2rem', display: 'flex', alignItems: 'center' }}>
          {label}
          <Button
            variant="outlined"
            size="small"
            onClick={onReset}
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 0.5,
              fontSize: '0.95rem',
              ml: 2,
              height: '2rem',
              lineHeight: 1,
              alignSelf: 'center',
              boxShadow: 'none',
              color: color,
              borderColor: color,
              '&:hover': {
                borderColor: color,
                backgroundColor: `${color}10`, // subtle tint on hover
              },
            }}
          >
            Reset
          </Button>
        </h3>
      </Box>
      <Slider
        value={[value.start, value.end]}
        onChange={(_, newValue) => onChange({ start: newValue[0], end: newValue[1] })}
        valueLabelDisplay="auto"
        min={0}
        max={100}
        sx={{
          color,
          '& .MuiSlider-thumb': {
            backgroundColor: '#fff',
            border: `2px solid ${color}`,
          },
          '& .MuiSlider-track': {
            backgroundColor: color,
          },
          '& .MuiSlider-rail': {
            backgroundColor: '#333',
          },
        }}
      />
      {/* Optionally render radio buttons for corners if provided (for future extensibility) */}
      {corners && corners.length > 0 && onCornerSelect && (
        <>
          {/* Dropdown for mobile */}
          <Box sx={{ mt: 2, width: '100%', display: { xs: 'block', md: 'none' } }}>
            <Select
              size="small"
              value={selectedCornerIdx !== null ? selectedCornerIdx : ''}
              displayEmpty
              onChange={e => onCornerSelect(Number(e.target.value))}
              sx={{ width: '100%', bgcolor: '#222', color: color, fontWeight: 700 }}
            >
              <MenuItem value="" disabled>Select Corner</MenuItem>
              {corners.map((corner, idx) => (
                <MenuItem key={idx} value={idx} sx={{ fontWeight: 700 }}>
                  {corner.name || `Corner ${idx+1}`}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* Button group for desktop/tablet */}
          <Box sx={{ mt: 2, display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 2 }}>
            {corners.map((corner, idx) => (
              <Button
                key={idx}
                variant={selectedCornerIdx === idx ? 'contained' : 'outlined'}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, minWidth: 80, mb: 1, bgcolor: selectedCornerIdx === idx ? color : 'transparent', color: selectedCornerIdx === idx ? '#fff' : color, borderColor: color, mr: 1 }}
                onClick={() => onCornerSelect(idx)}
              >
                {corner.name || `Corner ${idx+1}`}
              </Button>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
